import { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isSafari() {
  const ua = navigator.userAgent;
  return /safari/i.test(ua) && !/crios|fxios|chrome|android/i.test(ua);
}

export function InstallButton({ className = "", variant = "solid" }) {
  const [canInstall, setCanInstall] = useState(!!window.__vmInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIOS, setShowIOS] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("vm-installable", onInstallable);
    window.addEventListener("vm-installed", onInstalled);
    return () => {
      window.removeEventListener("vm-installable", onInstallable);
      window.removeEventListener("vm-installed", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    const prompt = window.__vmInstallPrompt;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") { setInstalled(true); setCanInstall(false); }
      window.__vmInstallPrompt = null;
      return;
    }
    if (isIOS()) { setShowIOS(true); return; }
    setShowHelp(true);
  };

  const base =
    variant === "solid"
      ? "bg-[#FF6A00] text-[#111] hover:bg-[#FFB27A]"
      : "border border-[#333] text-[#F5F5F5] hover:border-[#FF6A00] hover:text-[#FF6A00]";

  return (
    <>
      <button
        onClick={handleClick}
        data-testid="install-app-btn"
        className={`flex items-center justify-center gap-2 rounded-full px-4 py-2.5 font-semibold transition-all active:scale-95 ${base} ${className}`}
      >
        <Download className="h-4 w-4" /> App downloaden
      </button>

      <Dialog open={showIOS} onOpenChange={setShowIOS}>
        <DialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]" data-testid="ios-install-overlay">
          <DialogHeader><DialogTitle className="font-display text-2xl">Zet op beginscherm</DialogTitle></DialogHeader>
          <ol className="space-y-4 py-2">
            <li className="flex items-center gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FF6A00] font-bold text-[#111]">1</span><span className="flex items-center gap-2">Tik op <Share className="h-4 w-4 text-[#FF6A00]" /> <b>Deel</b></span></li>
            <li className="flex items-center gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FF6A00] font-bold text-[#111]">2</span><span className="flex items-center gap-2">Kies <Plus className="h-4 w-4 text-[#FF6A00]" /> <b>"Zet op beginscherm"</b></span></li>
            <li className="flex items-center gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FF6A00] font-bold text-[#111]">3</span><span>Tik op <b>"Voeg toe"</b></span></li>
          </ol>
        </DialogContent>
      </Dialog>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]" data-testid="install-help-overlay">
          <DialogHeader><DialogTitle className="font-display text-2xl">App installeren</DialogTitle></DialogHeader>
          <p className="py-2 text-[#cfcfcf]">
            Open VRIJMARKT in <b>Chrome</b> (Android) of <b>Safari</b> (iPhone/iPad) om de app op je beginscherm te zetten.
            Kies daar in het menu "Toevoegen aan startscherm".
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
